import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  FileInfo,
  FileValidationResult,
  FileValidationRule,
  SupportedFileType,
  ValidationConfig,
} from '../kyc-upload.types';

@Injectable({
  providedIn: 'root',
})
export class KycFileValidationService {
  private readonly DEFAULT_CONFIG: ValidationConfig = {
    maxFileSizeMB: 50,
    allowedImageTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/heic'],
    allowedPdfTypes: ['application/pdf'],
  };

  constructor(private readonly translate: TranslateService) {}

  /**
   * Validates a file for KYC upload
   */
  async validateFile(
    file: File,
    config: Partial<ValidationConfig> = {}
  ): Promise<FileValidationResult> {
    const finalConfig = { ...this.DEFAULT_CONFIG, ...config };
    const errors: FileValidationRule[] = [];
    const warnings: FileValidationRule[] = [];

    // Check file size
    const sizeCheck = this.validateFileSize(file, finalConfig.maxFileSizeMB);
    if (!sizeCheck.valid) {
      errors.push(...sizeCheck.errors);
    }

    // Check file type
    const typeCheck = this.validateFileType(
      file,
      finalConfig.allowedImageTypes,
      finalConfig.allowedPdfTypes
    );
    if (!typeCheck.valid) {
      errors.push(...typeCheck.errors);
      // If type is invalid, skip further checks
      return {
        valid: false,
        errors,
        warnings,
      };
    }

    // Check if file is a PDF
    if (this.isPdfFile(file)) {
      try {
        const pdfCheck = await this.validatePdf(file);
        if (!pdfCheck.valid) {
          errors.push(...pdfCheck.errors);
        }
        warnings.push(...pdfCheck.warnings);
      } catch (error) {
        errors.push({
          rule: 'PDF_VALIDATION',
          message: this.translate.instant('KycPdfValidationError'),
          severity: 'error',
        });
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * Validates multiple files
   */
  async validateFiles(
    files: File[],
    config: Partial<ValidationConfig> = {}
  ): Promise<Map<string, FileValidationResult>> {
    const results = new Map<string, FileValidationResult>();

    for (const file of files) {
      const result = await this.validateFile(file, config);
      results.set(file.name, result);
    }

    return results;
  }

  /**
   * Gets file info including type and preview
   */
  async getFileInfo(file: File): Promise<FileInfo> {
    const type = this.getFileType(file);
    let preview: string | undefined;

    if (type === 'image') {
      preview = await this.generateImagePreview(file);
    } else if (type === 'pdf') {
      // For PDF, we'll generate a placeholder or first page preview
      preview = await this.generatePdfPreview(file);
    }

    return {
      file,
      type,
      preview,
      size: file.size,
      name: file.name,
    };
  }

  // ==================== PRIVATE VALIDATION METHODS ====================

  private validateFileSize(
    file: File,
    maxSizeMB: number
  ): FileValidationResult {
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    const errors: FileValidationRule[] = [];

    if (file.size > maxSizeBytes) {
      errors.push({
        rule: 'FILE_TOO_LARGE',
        message: this.translate.instant('FileTooLargeMax', { size: maxSizeMB }),
        severity: 'error',
      });
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings: [],
    };
  }

  private validateFileType(
    file: File,
    allowedImageTypes: string[],
    allowedPdfTypes: string[]
  ): FileValidationResult {
    const errors: FileValidationRule[] = [];
    const allowedTypes = [...allowedImageTypes, ...allowedPdfTypes];

    if (!allowedTypes.includes(file.type)) {
      errors.push({
        rule: 'FILE_TYPE',
        message: this.translate.instant('KycFileTypeInvalid', {
          type: file.type || this.translate.instant('KycFileTypeUnknown'),
        }),
        severity: 'error',
      });
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings: [],
    };
  }

  private async validatePdf(file: File): Promise<FileValidationResult> {
    const errors: FileValidationRule[] = [];
    const warnings: FileValidationRule[] = [];

    try {
      // Basic PDF validation - check if it starts with PDF magic number
      const header = await this.readFileHeader(file);
      if (!header.startsWith('%PDF')) {
        errors.push({
          rule: 'PDF_INVALID',
          message: this.translate.instant('KycPdfInvalidFile'),
          severity: 'error',
        });
      }
    } catch (error) {
      errors.push({
        rule: 'PDF_READ',
        message: this.translate.instant('KycPdfReadError'),
        severity: 'error',
      });
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
    };
  }

  // ==================== HELPER METHODS ====================

  private isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  private isPdfFile(file: File): boolean {
    return file.type === 'application/pdf';
  }

  private getFileType(file: File): SupportedFileType {
    if (this.isImageFile(file)) return 'image';
    if (this.isPdfFile(file)) return 'pdf';
    return 'image'; // Default fallback
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  private async generateImagePreview(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        resolve(e.target?.result as string);
      };

      reader.onerror = () => {
        reject(new Error(this.translate.instant('FailedToReadFile')));
      };

      reader.readAsDataURL(file);
    });
  }

  private async generatePdfPreview(file: File): Promise<string> {
    // For now, return a placeholder icon
    // In the future, we could use PDF.js to generate a thumbnail of the first page
    return 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48cGF0aCBmaWxsPSJjdXJyZW50Q29sb3IiIGQ9Ik02IDJhMiAyIDAgMCAwLTIgMnYxNmEyIDIgMCAwIDAgMiAyaDEyYTIgMiAwIDAgMCAyLTJWOGwtNi02SDZ6Ii8+PC9zdmc+';
  }

  private readFileHeader(file: File, bytes: number = 5): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const blob = file.slice(0, bytes);

      reader.onload = (e) => {
        const arr = new Uint8Array(e.target?.result as ArrayBuffer);
        let header = '';
        for (let i = 0; i < arr.length; i++) {
          header += String.fromCharCode(arr[i]);
        }
        resolve(header);
      };

      reader.onerror = () => {
        reject(new Error(this.translate.instant('FailedToReadFile')));
      };

      reader.readAsArrayBuffer(blob);
    });
  }
}
